import {
  workflowDeadlineInstantTicksV2,
  workflowDeadlineV2,
  type WorkflowDeadlineReadV2,
} from '@app/shared/workflow-deadlines/workflow-deadline-v2.models';
import type { WorkflowDeadlineBinding } from '@app/shared/workflow-deadlines/workflow-deadline.models';
import { resolveTimerReasonCatalog } from '@app/shared/workflow-commands/workflow-reason-catalog';
import type {
  WorkflowTimerOperation,
  WorkflowTimerRead,
} from './workflow-timers.models';

export interface WorkflowTimerReadV2 extends Omit<
  WorkflowTimerRead,
  'warningDueUtc' | 'deadline' | 'availableActions'
> {
  version: 2;
  ruleVersion: 1 | 2;
  hasWarningAction: boolean;
  hasBreachAction: boolean;
  warningDueUtc: string | null;
  availableActions: WorkflowTimerOperation[];
  deadline: WorkflowDeadlineReadV2;
  controlsUnavailableReasonCode: string | null;
}

const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
const text = (value: unknown, maximum: number): value is string =>
  typeof value === 'string' &&
  !!value.trim() &&
  value === value.trim() &&
  value.length <= maximum &&
  ![...value].some(
    (character) =>
      character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127,
  );
const operations = ['pause', 'resume', 'reset'];

/** Timer2 is a selected contract with required evidence, never a reinterpretation of a Timer1 response. */
export function workflowTimerV2(
  value: unknown,
  binding: WorkflowDeadlineBinding | undefined,
): value is WorkflowTimerReadV2 {
  if (
    !record(value) ||
    !binding ||
    value['version'] !== 2 ||
    !workflowDeadlineV2(value['deadline'], binding)
  )
    return false;
  const deadline = value['deadline'];
  if (
    value['id'] !== deadline.sourceId ||
    value['code'] !== deadline.ruleCode ||
    value['label'] !== deadline.ruleLabel ||
    value['generation'] !== deadline.generation ||
    value['revision'] !== deadline.sourceRevision ||
    value['state'] !== deadline.state ||
    value['ruleVersion'] !== deadline.ruleVersion ||
    value['hasWarningAction'] !== deadline.hasWarningAction ||
    value['hasBreachAction'] !== deadline.hasBreachAction ||
    value['anchorUtc'] !== deadline.anchorUtc ||
    value['warningDueUtc'] !== deadline.warningDueUtc ||
    value['breachDueUtc'] !== deadline.breachDueUtc ||
    value['calendarCode'] !== deadline.calendarCode ||
    value['calendarVersionCode'] !== deadline.calendarVersionCode ||
    !(value['issueCode'] === null || text(value['issueCode'], 120)) ||
    !(
      value['controlsUnavailableReasonCode'] === null ||
      text(value['controlsUnavailableReasonCode'], 120)
    ) ||
    !Array.isArray(value['availableActions']) ||
    !value['availableActions'].every(
      (action) => typeof action === 'string' && operations.includes(action),
    ) ||
    new Set(value['availableActions']).size !==
      value['availableActions'].length ||
    typeof value['eTag'] !== 'string' ||
    value['eTag'].length > 200
  )
    return false;
  const actions = value['availableActions'] as WorkflowTimerOperation[];
  const token = /^"workflow-(\d+)-(\d+)-step-(\d+)-(\d+)"$/.exec(value['eTag']);
  if (
    value['eTag'] !== '' &&
    (!token ||
      Number(token[1]) !== binding.workflowInstanceId ||
      Number(token[3]) !== binding.occurrenceId ||
      ![token[1], token[2], token[3], token[4]].every(
        (part) => Number.isSafeInteger(Number(part)) && Number(part) > 0,
      ))
  )
    return false;
  if (
    actions.length &&
    (!token ||
      !deadline.isCurrent ||
      (value['state'] !== 'active' && value['state'] !== 'paused'))
  )
    return false;
  if (value['controlsUnavailableReasonCode'] !== null && actions.length)
    return false;
  if (
    value['controlsUnavailableReasonCode'] ===
      'workflow.timer.optional_stages_disabled' &&
    value['ruleVersion'] !== 2
  )
    return false;

  const asOf = workflowDeadlineInstantTicksV2(deadline.asOfUtc)!;
  const anchor = workflowDeadlineInstantTicksV2(deadline.anchorUtc)!;
  const warningDue = workflowDeadlineInstantTicksV2(deadline.warningDueUtc);
  const breachDue = workflowDeadlineInstantTicksV2(deadline.breachDueUtc)!;
  const nullableInstant = (key: string): bigint | null | false =>
    value[key] === null
      ? null
      : (workflowDeadlineInstantTicksV2(value[key]) ?? false);
  const paused = nullableInstant('pausedUtc'),
    warning = nullableInstant('warningCapturedUtc'),
    breach = nullableInstant('breachCapturedUtc');
  if (
    paused === false ||
    warning === false ||
    breach === false ||
    (value['state'] === 'paused') !== (paused !== null) ||
    (paused !== null &&
      (paused < anchor ||
        paused > asOf ||
        warningDue === null ||
        paused >= warningDue ||
        warning !== null ||
        breach !== null)) ||
    (warning !== null &&
      (warningDue === null || warning < warningDue || warning > asOf)) ||
    (breach !== null &&
      (!deadline.hasBreachAction || breach < breachDue || breach > asOf))
  )
    return false;
  if (deadline.isCurrent && value['state'] === 'active') {
    if (
      breach !== null
        ? deadline.status !== 'breach_captured'
        : deadline.status === 'breach_captured' ||
          (warning !== null && asOf < breachDue
            ? deadline.status !== 'warning_captured'
            : deadline.status === 'warning_captured')
    )
      return false;
  }
  if (
    (actions.includes('pause') &&
      (value['state'] !== 'active' ||
        warningDue === null ||
        asOf >= warningDue ||
        warning !== null ||
        breach !== null)) ||
    (actions.includes('resume') &&
      (value['state'] !== 'paused' ||
        warningDue === null ||
        warning !== null ||
        breach !== null)) ||
    (actions.includes('reset') &&
      value['state'] !== 'active' &&
      value['state'] !== 'paused')
  )
    return false;

  const catalogs = value['reasonCatalogs'];
  if (
    resolveTimerReasonCatalog(catalogs, 'reset', {
      workflowInstanceId: binding.workflowInstanceId,
      occurrenceId: binding.occurrenceId,
      timerId: deadline.sourceId,
      timerRevision: deadline.sourceRevision,
      availableOperations: actions,
    }).mode === 'unavailable'
  )
    return false;
  if (Array.isArray(catalogs)) {
    let scope: string | undefined;
    for (const catalog of catalogs) {
      if (!record(catalog) || !record(catalog['context'])) return false;
      const context = catalog['context'];
      if (
        context['definitionVersionId'] !== deadline.definitionVersionId ||
        context['configurationVersionId'] !== deadline.configurationVersionId
      )
        return false;
      const contextScope = JSON.stringify([
        context['scopeTypeCode'],
        context['scopeKey'],
      ]);
      if (scope !== undefined && scope !== contextScope) return false;
      scope = contextScope;
    }
  }
  return true;
}

/** All generations are retained; unique timer IDs and code/generation pairs must not be flattened. */
export function workflowTimerListV2(
  value: unknown,
  binding: WorkflowDeadlineBinding | undefined,
): value is WorkflowTimerReadV2[] {
  if (
    !Array.isArray(value) ||
    !binding ||
    !Number.isSafeInteger(binding.workflowInstanceId) ||
    binding.workflowInstanceId < 1 ||
    !Number.isSafeInteger(binding.occurrenceId) ||
    binding.occurrenceId < 1 ||
    !value.every((row) => workflowTimerV2(row, binding))
  )
    return false;
  const rows: WorkflowTimerReadV2[] = value;
  if (
    new Set(rows.map((row) => row.id.toLowerCase())).size !== rows.length ||
    new Set(rows.map((row) => JSON.stringify([row.code, row.generation])))
      .size !== rows.length ||
    rows.some(
      (row) =>
        row.deadline.asOfUtc !== rows[0].deadline.asOfUtc ||
        row.deadline.definitionVersionId !==
          rows[0].deadline.definitionVersionId ||
        row.deadline.configurationVersionId !==
          rows[0].deadline.configurationVersionId ||
        row.deadline.definitionHash !== rows[0].deadline.definitionHash,
    )
  )
    return false;
  for (const code of new Set(rows.map((row) => row.code))) {
    const generations = rows.filter((row) => row.code === code),
      current = generations.filter((row) => row.deadline.isCurrent);
    if (
      current.length > 1 ||
      (current.length === 1 &&
        current[0].generation !==
          generations.reduce(
            (maximum, row) => Math.max(maximum, row.generation),
            0,
          ))
    )
      return false;
  }
  return true;
}
